import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const MEDIA_URL  = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';
const TYPES      = ['physical','digital','bundle'];
const STATUSES   = ['draft','active','out_of_stock','archived'];
const TYPE_COLORS   = { physical:'#e8a43a', digital:'#4e9cf5', bundle:'#3ecf8e' };
const STATUS_COLORS = { active:'#3ecf8e', draft:'#6b7280', out_of_stock:'#f59e0b', archived:'#ef4444' };

function ColorBadge({ val, map }) {
  const color = map[val] || '#6b7280';
  return (
    <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'3px 10px', borderRadius:20,
      background:`${color}22`, color, border:`1px solid ${color}44`, textTransform:'capitalize' }}>
      {val?.replace(/_/g,' ')}
    </span>
  );
}

// ── Multi-image Media Manager ─────────────────────────────
function MediaManager({ primary, gallery, onPrimaryChange, onGalleryChange }) {
  const [uploading, setUploading] = useState(null); // 'primary' | number index
  const [showUrl,   setShowUrl]   = useState(false);
  const [urlInput,  setUrlInput]  = useState('');
  const token = localStorage.getItem('mh_token');
  const MAX_GALLERY = 5;

  const uploadFile = async (file, target) => {
    setUploading(target);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${MEDIA_URL}/api/media/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      const url = data.data?.file_url || data.file_url;
      if (target === 'primary') {
        onPrimaryChange(url);
      } else {
        const updated = [...gallery];
        updated[target] = url;
        onGalleryChange(updated.filter(Boolean));
      }
    } catch(e) { alert('Upload failed: ' + e.message); }
    finally { setUploading(null); }
  };

  const removeGallery = (i) => onGalleryChange(gallery.filter((_,idx) => idx !== i));

  const addByUrl = () => {
    const url = urlInput.trim();
    if (!url) return;
    if (!primary) onPrimaryChange(url);
    else if (gallery.length < MAX_GALLERY) onGalleryChange([...gallery, url]);
    else alert('Maximum gallery images reached');
    setUrlInput(''); setShowUrl(false);
  };

  // Individual image slot
  const Slot = ({ url, label, target, onRemove, isPrimary }) => (
    <div style={{ position:'relative', borderRadius:10, overflow:'hidden', background:'var(--bg-elevated)', border:'1px solid var(--border)', aspectRatio:'4/3', cursor:'pointer' }}>
      {url ? (
        <>
          <img src={url} alt={label} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} onError={e=>e.target.style.opacity=0.2}/>
          {/* Hover overlay */}
          <div className="slot-overlay" style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0)', transition:'background 0.2s', display:'flex', flexDirection:'column', justifyContent:'flex-end', padding:6, gap:4 }}>
            <label style={{ padding:'4px 8px', borderRadius:6, background:'rgba(13,15,18,0.85)', color:'#fff', fontSize:'0.68rem', cursor:'pointer', fontFamily:'inherit', textAlign:'center', display:'block' }}>
              {uploading===target ? '⏳' : '⬆ Replace'}
              <input type="file" accept="image/*,video/*" style={{ display:'none' }} onChange={e=>uploadFile(e.target.files[0], target)}/>
            </label>
            {onRemove && (
              <button onClick={onRemove} style={{ padding:'4px 8px', borderRadius:6, background:'rgba(240,96,96,0.85)', color:'#fff', fontSize:'0.68rem', border:'none', cursor:'pointer', fontFamily:'inherit' }}>✕ Remove</button>
            )}
          </div>
          {isPrimary && (
            <span style={{ position:'absolute', top:6, left:6, fontSize:'0.58rem', fontWeight:700, padding:'2px 8px', borderRadius:20, background:'rgba(232,164,58,0.92)', color:'#080a0d' }}>PRIMARY</span>
          )}
        </>
      ) : (
        <label style={{ width:'100%', height:'100%', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', cursor:'pointer', gap:6 }}>
          {uploading===target ? (
            <span style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>Uploading…</span>
          ) : (
            <>
              <span style={{ fontSize:'1.6rem', opacity:0.2 }}>◈</span>
              <span style={{ fontSize:'0.68rem', color:'var(--text-muted)', textAlign:'center', lineHeight:1.4 }}>{label}</span>
            </>
          )}
          <input type="file" accept="image/*,video/*" style={{ display:'none' }} onChange={e=>uploadFile(e.target.files[0], target)}/>
        </label>
      )}
    </div>
  );

  return (
    <div>
      <style>{`.slot-overlay:hover { background: rgba(0,0,0,0.5) !important; }`}</style>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <label style={{ fontSize:'0.7rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>
          Product Media
          <span style={{ fontWeight:400, marginLeft:6 }}>— primary + up to {MAX_GALLERY} gallery images</span>
        </label>
        <button onClick={() => setShowUrl(v=>!v)}
          style={{ fontSize:'0.72rem', color:'var(--accent)', background:'none', border:'none', cursor:'pointer', padding:0 }}>
          {showUrl ? '✕ Cancel' : '+ Add by URL'}
        </button>
      </div>

      {/* URL input */}
      {showUrl && (
        <div style={{ display:'flex', gap:8, marginBottom:12 }}>
          <input value={urlInput} onChange={e=>setUrlInput(e.target.value)}
            placeholder="https://... or /uploads/filename.jpg"
            onKeyDown={e=>e.key==='Enter'&&addByUrl()}
            style={{ flex:1, background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-primary)', padding:'8px 12px', fontSize:'0.83rem', outline:'none', fontFamily:'inherit' }}/>
          <button onClick={addByUrl} style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'var(--accent)', color:'#0d0f12', fontWeight:700, cursor:'pointer', fontSize:'0.83rem', fontFamily:'inherit' }}>Add</button>
        </div>
      )}

      {/* Image grid — primary is 2x wider */}
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gridTemplateRows:'auto auto', gap:8 }}>
        {/* Primary — spans 2 rows */}
        <div style={{ gridRow:'1 / 3' }}>
          <Slot url={primary} label="Drop or click to upload primary image" target="primary" isPrimary onRemove={primary?()=>onPrimaryChange(''):null}/>
        </div>
        {/* Gallery slots 0–3 in 2 columns */}
        {Array.from({length: 4}).map((_,i) => (
          <Slot key={i} url={gallery[i]||''} label={`Gallery ${i+1}`} target={i}
            onRemove={gallery[i] ? ()=>removeGallery(i) : null}/>
        ))}
        {/* 5th gallery slot on its own row if we want it */}
        {gallery.length >= 4 && (
          <div style={{ gridColumn:'2 / 4' }}>
            <Slot url={gallery[4]||''} label="Gallery 5" target={4} onRemove={gallery[4]?()=>removeGallery(4):null}/>
          </div>
        )}
      </div>

      {/* URL list */}
      {(primary||gallery.length>0) && (
        <div style={{ marginTop:10, display:'flex', flexDirection:'column', gap:3 }}>
          {[...(primary?[primary]:[]), ...gallery].map((url,i)=>(
            <div key={i} style={{ display:'flex', gap:8, alignItems:'center', background:'var(--bg-elevated)', padding:'4px 10px', borderRadius:6, fontSize:'0.7rem', fontFamily:'monospace' }}>
              {i===0&&primary&&<span style={{ color:'var(--accent)', fontWeight:700, fontFamily:'sans-serif', flexShrink:0 }}>PRIMARY</span>}
              <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'var(--text-muted)' }}>{url}</span>
              <button onClick={()=>{ if(i===0&&primary) onPrimaryChange(''); else removeGallery(primary?i-1:i); }}
                style={{ color:'#ef4444', background:'none', border:'none', cursor:'pointer', fontSize:'0.75rem', padding:0, lineHeight:1, flexShrink:0 }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Product Form Modal ────────────────────────────────────
function ProductModal({ product, onClose, onSaved }) {
  const isEdit = !!product?.id;
  const [form, setForm] = useState({
    sku:'', name:'', slug:'', product_type:'physical', category:'',
    description:'', short_desc:'', price:'', currency:'RWF',
    compare_price:'', is_free:false, status:'active',
    is_public:true, is_featured:false, stock_qty:'',
    media_url:'', media_urls:[], preview_url:'', download_url:'',
    tags:'', tech_stack:'', sizes:'', colors:'', order_index:0,
    ...product,
    media_urls: Array.isArray(product?.media_urls) ? product.media_urls : [],
    tags:       Array.isArray(product?.tags)        ? product.tags.join(', ')       : (product?.tags||''),
    tech_stack: Array.isArray(product?.tech_stack)  ? product.tech_stack.join(', ') : (product?.tech_stack||''),
    sizes:      Array.isArray(product?.sizes)        ? product.sizes.join(', ')      : (product?.sizes||''),
    colors:     Array.isArray(product?.colors)       ? product.colors.join(', ')     : (product?.colors||''),
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const autoSlug = n => n.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');

  const save = async () => {
    if (!form.sku||!form.name||!form.slug) { setError('SKU, Name and Slug are required'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        price:         Number(form.price)||0,
        compare_price: form.compare_price ? Number(form.compare_price) : null,
        stock_qty:     form.stock_qty!=='' ? Number(form.stock_qty) : null,
        media_urls:    form.media_urls || [],
        tags:          form.tags.split(',').map(t=>t.trim()).filter(Boolean),
        tech_stack:    form.tech_stack.split(',').map(t=>t.trim()).filter(Boolean),
        sizes:         form.sizes.split(',').map(t=>t.trim()).filter(Boolean),
        colors:        form.colors.split(',').map(t=>t.trim()).filter(Boolean),
      };
      if (isEdit) await api.put(`/admin/products/${product.id}`, payload);
      else        await api.post('/admin/products', payload);
      onSaved();
    } catch(e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const inp = { width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-primary)', padding:'9px 12px', fontSize:'0.85rem', outline:'none', boxSizing:'border-box', fontFamily:'inherit' };
  const ta  = { ...inp, resize:'vertical', minHeight:80 };
  const lbl = { fontSize:'0.7rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:5, marginTop:12 };

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:600, background:'rgba(0,0,0,0.75)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:16, width:'100%', maxWidth:740, maxHeight:'92vh', overflow:'auto', boxShadow:'0 32px 80px rgba(0,0,0,0.5)' }}>

        {/* Sticky header */}
        <div style={{ padding:'20px 28px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, background:'var(--bg-surface)', zIndex:10 }}>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.1rem', fontWeight:600 }}>{isEdit?`Edit: ${product.name}`:'New Product'}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-muted)', fontSize:'1.3rem', cursor:'pointer', lineHeight:1, padding:4 }}>✕</button>
        </div>

        <div style={{ padding:'20px 28px', display:'flex', flexDirection:'column', gap:0 }}>

          {/* ── Media Manager ── */}
          <div style={{ marginBottom:20, padding:16, background:'var(--bg-elevated)', borderRadius:12, border:'1px solid var(--border)' }}>
            <MediaManager
              primary={form.media_url}
              gallery={form.media_urls}
              onPrimaryChange={url => set('media_url', url)}
              onGalleryChange={urls => set('media_urls', urls)}
            />
          </div>

          {/* ── Core fields ── */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <div><label style={lbl}>SKU *</label><input value={form.sku} onChange={e=>set('sku',e.target.value)} placeholder="MH-TEE-001" style={inp}/></div>
            <div><label style={lbl}>Type</label>
              <select value={form.product_type} onChange={e=>set('product_type',e.target.value)} style={inp}>
                {TYPES.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Status</label>
              <select value={form.status} onChange={e=>set('status',e.target.value)} style={inp}>
                {STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <label style={lbl}>Product Name *</label>
          <input value={form.name} onChange={e=>{set('name',e.target.value);if(!isEdit)set('slug',autoSlug(e.target.value));}} placeholder="MilleHills Classic Tee" style={inp}/>

          <label style={lbl}>Slug *</label>
          <input value={form.slug} onChange={e=>set('slug',e.target.value)} placeholder="millehills-classic-tee" style={inp}/>

          <label style={lbl}>Category</label>
          <input value={form.category||''} onChange={e=>set('category',e.target.value)} placeholder="Apparel / Web Templates / Design Assets" style={inp}/>

          <label style={lbl}>Short Description (shown on card)</label>
          <input value={form.short_desc||''} onChange={e=>set('short_desc',e.target.value)} placeholder="One-liner for the product card" style={inp}/>

          <label style={lbl}>Full Description</label>
          <textarea value={form.description||''} onChange={e=>set('description',e.target.value)} placeholder="Full product details…" style={ta}/>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <div><label style={lbl}>Price (RWF)</label><input type="number" value={form.price} onChange={e=>set('price',e.target.value)} placeholder="15000" style={inp}/></div>
            <div><label style={lbl}>Compare Price</label><input type="number" value={form.compare_price||''} onChange={e=>set('compare_price',e.target.value)} placeholder="Original price" style={inp}/></div>
            <div><label style={lbl}>Stock Qty</label><input type="number" value={form.stock_qty||''} onChange={e=>set('stock_qty',e.target.value)} placeholder="Empty = unlimited" style={inp}/></div>
          </div>

          {/* Digital-specific */}
          {form.product_type==='digital' && <>
            <label style={lbl}>Preview / Demo URL</label>
            <input value={form.preview_url||''} onChange={e=>set('preview_url',e.target.value)} placeholder="https://demo.example.com" style={inp}/>
            <label style={lbl}>Download URL (after purchase)</label>
            <input value={form.download_url||''} onChange={e=>set('download_url',e.target.value)} placeholder="https://..." style={inp}/>
            <label style={lbl}>Tech Stack (comma-separated)</label>
            <input value={form.tech_stack||''} onChange={e=>set('tech_stack',e.target.value)} placeholder="React, Node.js, PostgreSQL" style={inp}/>
          </>}

          {/* Physical-specific */}
          {form.product_type==='physical' && <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><label style={lbl}>Sizes (comma-separated)</label><input value={form.sizes||''} onChange={e=>set('sizes',e.target.value)} placeholder="S, M, L, XL, XXL" style={inp}/></div>
              <div><label style={lbl}>Colors (comma-separated)</label><input value={form.colors||''} onChange={e=>set('colors',e.target.value)} placeholder="Black, White, Navy" style={inp}/></div>
            </div>
          </>}

          <label style={lbl}>Tags (comma-separated)</label>
          <input value={form.tags||''} onChange={e=>set('tags',e.target.value)} placeholder="merch, apparel, react, template" style={inp}/>

          <div style={{ display:'flex', gap:20, marginTop:14, flexWrap:'wrap' }}>
            {[['is_free','Free product'],['is_public','Public (visible on site)'],['is_featured','Featured']].map(([k,l])=>(
              <label key={k} style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.85rem', cursor:'pointer', color:'var(--text-secondary)' }}>
                <input type="checkbox" checked={!!form[k]} onChange={e=>set(k,e.target.checked)}/>{l}
              </label>
            ))}
          </div>

          {error && <div style={{ marginTop:14, padding:'10px 14px', background:'rgba(240,96,96,0.1)', border:'1px solid rgba(240,96,96,0.3)', borderRadius:8, fontSize:'0.85rem', color:'#ef4444' }}>⚠ {error}</div>}

          <div style={{ display:'flex', gap:10, marginTop:20 }}>
            <button onClick={onClose} style={{ flex:1, padding:'11px', borderRadius:8, border:'1px solid var(--border)', background:'transparent', color:'var(--text-secondary)', cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
            <button onClick={save} disabled={saving} style={{ flex:2, padding:'11px', borderRadius:8, border:'none', background:'var(--accent)', color:'#0d0f12', fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
              {saving?'Saving…':isEdit?'Save Changes':'Create Product'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Order Modal ───────────────────────────────────────────
function OrderModal({ order, onClose, onUpdate }) {
  const [status,  setStatus]  = useState(order.status);
  const [payment, setPayment] = useState(order.payment_status);
  const [saving,  setSaving]  = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.put(`/admin/product-orders/${order.id}`, { status, payment_status:payment }); onUpdate(); }
    finally { setSaving(false); }
  };
  const row = (label, value) => (
    <div key={label} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.83rem', padding:'6px 0', borderBottom:'1px solid var(--border)' }}>
      <span style={{ color:'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight:500, color:'var(--text-primary)', textAlign:'right', maxWidth:'60%' }}>{value||'—'}</span>
    </div>
  );
  const sel = { width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-primary)', padding:'9px 12px', fontSize:'0.85rem', outline:'none' };
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:600, background:'rgba(0,0,0,0.75)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:16, width:'100%', maxWidth:480, maxHeight:'90vh', overflow:'auto', boxShadow:'0 32px 80px rgba(0,0,0,0.5)' }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ fontFamily:'var(--font-display)', fontWeight:600 }}>Order #{order.id.slice(-8).toUpperCase()}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-muted)', fontSize:'1.2rem', cursor:'pointer', lineHeight:1, padding:4 }}>✕</button>
        </div>
        <div style={{ padding:'20px 24px' }}>
          {row('Product',  order.product_name)}
          {row('Customer', order.full_name)}
          {row('Email',    order.email)}
          {row('Phone',    order.phone)}
          {row('Quantity', order.quantity)}
          {row('Size',     order.size)}
          {row('Color',    order.color)}
          {row('Total',    `${order.currency} ${Number(order.total_price).toLocaleString()}`)}
          {row('Address',  order.shipping_address)}
          {row('Notes',    order.notes)}
          {row('Placed',   new Date(order.created_at).toLocaleString('en-RW'))}
          <div style={{ marginTop:16, display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={{ fontSize:'0.7rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:5 }}>Order Status</label>
              <select value={status} onChange={e=>setStatus(e.target.value)} style={sel}>
                {['pending','confirmed','shipped','delivered','cancelled'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:'0.7rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:5 }}>Payment</label>
              <select value={payment} onChange={e=>setPayment(e.target.value)} style={sel}>
                {['unpaid','paid','refunded'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <button onClick={save} disabled={saving} style={{ marginTop:16, width:'100%', padding:'11px', borderRadius:8, border:'none', background:'var(--accent)', color:'#0d0f12', fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
            {saving?'Saving…':'Update Order'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────
export default function ProductsPage() {
  const [tab,          setTab]          = useState('products');
  const [products,     setProducts]     = useState([]);
  const [orders,       setOrders]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [modal,        setModal]        = useState(null);
  const [viewMode,     setViewMode]     = useState('grid');
  const [typeFilter,   setTypeFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search,       setSearch]       = useState('');

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter)   params.type   = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await api.get('/admin/products', { params });
      setProducts(res.data?.data || []);
    } catch { setProducts([]); } finally { setLoading(false); }
  }, [typeFilter, statusFilter]);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/product-orders');
      setOrders(res.data?.data || []);
    } catch { setOrders([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (tab==='products') loadProducts();
    else loadOrders();
  }, [tab, loadProducts, loadOrders]);

  const archive = async (id) => {
    if (!window.confirm('Archive this product?')) return;
    await api.delete(`/admin/products/${id}`);
    loadProducts();
  };

  const filtered = products.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.sku.toLowerCase().includes(search.toLowerCase())
  );

  const tabBtn = t => ({
    padding:'10px 20px', fontSize:'0.88rem', cursor:'pointer', background:'none', border:'none',
    borderBottom:`2px solid ${tab===t?'var(--accent)':'transparent'}`,
    color:tab===t?'var(--accent)':'var(--text-secondary)', fontWeight:tab===t?600:400,
    transition:'all 0.15s', marginBottom:-1,
  });
  const filterInp = { background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-primary)', padding:'8px 14px', fontSize:'0.85rem', outline:'none', cursor:'pointer' };

  return (
    <div style={{ padding:'28px 32px', maxWidth:1200 }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:'1.5rem', fontWeight:700, marginBottom:4 }}>Products</h1>
          <p style={{ fontSize:'0.85rem', color:'var(--text-muted)' }}>Manage physical products, digital downloads, and orders</p>
        </div>
        {tab==='products' && (
          <button onClick={()=>setModal({type:'product',data:null})}
            style={{ padding:'10px 20px', borderRadius:10, border:'none', background:'var(--accent)', color:'#0d0f12', fontWeight:700, cursor:'pointer', fontSize:'0.88rem', fontFamily:'inherit' }}>
            + New Product
          </button>
        )}
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 }}>
        {[
          { label:'Total Products', val:products.length,                                       color:'var(--accent)' },
          { label:'Active',         val:products.filter(p=>p.status==='active').length,         color:'#3ecf8e' },
          { label:'Physical',       val:products.filter(p=>p.product_type==='physical').length, color:'#e8a43a' },
          { label:'Digital',        val:products.filter(p=>p.product_type==='digital').length,  color:'#4e9cf5' },
        ].map(s=>(
          <div key={s.label} style={{ padding:'16px 20px', background:'var(--bg-surface)', borderRadius:12, border:'1px solid var(--border)' }}>
            <div style={{ fontSize:'1.6rem', fontWeight:700, color:s.color, fontFamily:'var(--font-display)' }}>{s.val}</div>
            <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:3 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ borderBottom:'1px solid var(--border)', marginBottom:20, display:'flex' }}>
        <button style={tabBtn('products')} onClick={()=>setTab('products')}>
          Products <span style={{ marginLeft:5, fontSize:'0.65rem', padding:'2px 7px', borderRadius:10, background:'rgba(232,164,58,0.1)', color:'var(--accent)' }}>{products.length}</span>
        </button>
        <button style={tabBtn('orders')} onClick={()=>setTab('orders')}>
          Orders <span style={{ marginLeft:5, fontSize:'0.65rem', padding:'2px 7px', borderRadius:10, background:'rgba(62,207,142,0.1)', color:'#3ecf8e' }}>{orders.length}</span>
        </button>
      </div>

      {tab==='products' && (
        <>
          {/* Filters + view toggle */}
          <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search products…" style={{ ...filterInp, width:220 }}/>
            <select value={typeFilter}   onChange={e=>setTypeFilter(e.target.value)}   style={filterInp}>
              <option value="">All Types</option>
              {TYPES.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
            <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={filterInp}>
              <option value="">All Statuses</option>
              {STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
            <span style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{filtered.length} product{filtered.length!==1?'s':''}</span>
            <div style={{ display:'flex', gap:4, marginLeft:'auto' }}>
              {[['grid','⊞'],['list','☰']].map(([v,icon])=>(
                <button key={v} onClick={()=>setViewMode(v)}
                  style={{ padding:'7px 12px', borderRadius:8, border:`1px solid ${viewMode===v?'var(--accent)':'var(--border)'}`, background:viewMode===v?'rgba(232,164,58,0.1)':'transparent', color:viewMode===v?'var(--accent)':'var(--text-muted)', cursor:'pointer', fontSize:'0.9rem', transition:'all 0.15s' }}>
                  {icon}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign:'center', padding:60, color:'var(--text-muted)' }}>Loading…</div>
          ) : filtered.length===0 ? (
            <div style={{ textAlign:'center', padding:60 }}>
              <div style={{ fontSize:'2rem', opacity:0.2, marginBottom:12 }}>◈</div>
              <div style={{ color:'var(--text-muted)', marginBottom:16 }}>No products found</div>
              <button onClick={()=>setModal({type:'product',data:null})} style={{ padding:'9px 20px', borderRadius:8, border:'1px solid var(--border)', background:'transparent', color:'var(--text-secondary)', cursor:'pointer', fontFamily:'inherit' }}>+ Create First Product</button>
            </div>
          ) : viewMode==='list' ? (
            <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.83rem' }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid var(--border)' }}>
                    {['','SKU','Name','Type','Status','Price','Stock','Orders',''].map(h=>(
                      <th key={h} style={{ padding:'10px 14px', textAlign:'left', color:'var(--text-muted)', fontSize:'0.7rem', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p=>(
                    <tr key={p.id} style={{ borderBottom:'1px solid var(--border)' }}
                      onMouseEnter={e=>e.currentTarget.style.background='var(--bg-elevated)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={{ padding:'10px 8px 10px 14px', width:48 }}>
                        <div style={{ width:40, height:40, borderRadius:8, overflow:'hidden', background:'var(--bg-elevated)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                          {p.media_url ? <img src={p.media_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/> : <span style={{ fontSize:'1rem', opacity:0.2 }}>◈</span>}
                        </div>
                      </td>
                      <td style={{ padding:'10px 14px', fontFamily:'monospace', fontSize:'0.72rem', color:'var(--text-muted)' }}>{p.sku}</td>
                      <td style={{ padding:'10px 14px', fontWeight:500 }}>
                        <div>{p.name}</div>
                        {p.media_urls?.length>0 && <div style={{ fontSize:'0.65rem', color:'var(--text-muted)', marginTop:2 }}>+{p.media_urls.length} gallery</div>}
                      </td>
                      <td style={{ padding:'10px 14px' }}><ColorBadge val={p.product_type} map={TYPE_COLORS}/></td>
                      <td style={{ padding:'10px 14px' }}><ColorBadge val={p.status} map={STATUS_COLORS}/></td>
                      <td style={{ padding:'10px 14px', fontWeight:600, color:'var(--accent)' }}>{p.is_free?'Free':`${p.currency} ${Number(p.price).toLocaleString()}`}</td>
                      <td style={{ padding:'10px 14px', color:'var(--text-muted)' }}>{p.stock_qty??'∞'}</td>
                      <td style={{ padding:'10px 14px', color:'var(--text-muted)' }}>{p.total_orders||0}</td>
                      <td style={{ padding:'10px 14px' }}>
                        <div style={{ display:'flex', gap:6 }}>
                          <button onClick={()=>setModal({type:'product',data:p})} style={{ padding:'5px 12px', borderRadius:6, border:'1px solid var(--border)', background:'transparent', color:'var(--text-secondary)', fontSize:'0.78rem', cursor:'pointer', fontFamily:'inherit' }}>Edit</button>
                          <button onClick={()=>archive(p.id)} style={{ padding:'5px 10px', borderRadius:6, border:'1px solid rgba(239,68,68,0.3)', background:'transparent', color:'#ef4444', fontSize:'0.78rem', cursor:'pointer', fontFamily:'inherit' }}>✕</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>
              {filtered.map(p=>(
                <div key={p.id} style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden' }}>
                  {/* Image with gallery indicator */}
                  <div style={{ height:150, background:'var(--bg-elevated)', position:'relative', overflow:'hidden' }}>
                    {p.media_url
                      ? <img src={p.media_url} alt={p.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e=>e.target.parentNode.style.background='var(--bg-elevated)'}/>
                      : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'2.5rem', opacity:0.12 }}>
                          {{'physical':'◉','digital':'◈','bundle':'◐'}[p.product_type]||'◈'}
                        </div>
                    }
                    <div style={{ position:'absolute', top:10, left:10 }}><ColorBadge val={p.product_type} map={TYPE_COLORS}/></div>
                    <div style={{ position:'absolute', top:10, right:10 }}><ColorBadge val={p.status} map={STATUS_COLORS}/></div>
                    {/* Gallery count badge */}
                    {p.media_urls?.length>0 && (
                      <div style={{ position:'absolute', bottom:8, right:8, fontSize:'0.62rem', fontWeight:700, padding:'2px 8px', borderRadius:20, background:'rgba(0,0,0,0.65)', color:'#fff' }}>
                        +{p.media_urls.length} more
                      </div>
                    )}
                  </div>
                  <div style={{ padding:'14px 16px' }}>
                    <div style={{ fontSize:'0.62rem', color:'var(--text-muted)', marginBottom:4, fontFamily:'monospace' }}>{p.sku}</div>
                    <h3 style={{ fontFamily:'var(--font-display)', fontSize:'0.95rem', fontWeight:600, marginBottom:6, lineHeight:1.3 }}>{p.name}</h3>
                    <div style={{ fontSize:'0.88rem', color:'var(--accent)', fontWeight:700, marginBottom:10 }}>
                      {p.is_free?'Free':`${p.currency} ${Number(p.price).toLocaleString()}`}
                      {p.compare_price>p.price && <span style={{ fontSize:'0.75rem', color:'var(--text-muted)', textDecoration:'line-through', marginLeft:6 }}>{Number(p.compare_price).toLocaleString()}</span>}
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.75rem', color:'var(--text-muted)', marginBottom:12 }}>
                      <span>{p.total_orders||0} orders</span>
                      {p.stock_qty!==null&&<span>Stock: {p.stock_qty}</span>}
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={()=>setModal({type:'product',data:p})} style={{ flex:1, padding:'8px', borderRadius:7, border:'1px solid var(--border)', background:'transparent', color:'var(--text-secondary)', fontSize:'0.8rem', cursor:'pointer', fontFamily:'inherit' }}>Edit</button>
                      <button onClick={()=>archive(p.id)} style={{ padding:'8px 12px', borderRadius:7, border:'1px solid rgba(240,96,96,0.3)', background:'transparent', color:'#ef4444', fontSize:'0.8rem', cursor:'pointer', fontFamily:'inherit' }}>Archive</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab==='orders' && (
        <div style={{ overflowX:'auto' }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:60, color:'var(--text-muted)' }}>Loading…</div>
          ) : orders.length===0 ? (
            <div style={{ textAlign:'center', padding:60, color:'var(--text-muted)' }}>No orders yet</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.83rem' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid var(--border)' }}>
                  {['Order','Product','Customer','Qty','Total','Status','Payment','Date',''].map(h=>(
                    <th key={h} style={{ padding:'10px 12px', textAlign:'left', color:'var(--text-muted)', fontSize:'0.72rem', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map(o=>(
                  <tr key={o.id} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:'12px', fontFamily:'monospace', fontSize:'0.75rem', color:'var(--text-muted)' }}>#{o.id.slice(-8).toUpperCase()}</td>
                    <td style={{ padding:'12px', fontWeight:500 }}>{o.product_name}</td>
                    <td style={{ padding:'12px' }}><div>{o.full_name}</div><div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{o.email}</div></td>
                    <td style={{ padding:'12px', textAlign:'center' }}>{o.quantity}</td>
                    <td style={{ padding:'12px', fontWeight:600, color:'var(--accent)' }}>{o.currency} {Number(o.total_price).toLocaleString()}</td>
                    <td style={{ padding:'12px' }}><ColorBadge val={o.status} map={{pending:'#6b7280',confirmed:'#4e9cf5',shipped:'#e8a43a',delivered:'#3ecf8e',cancelled:'#ef4444'}}/></td>
                    <td style={{ padding:'12px' }}><ColorBadge val={o.payment_status} map={{unpaid:'#6b7280',paid:'#3ecf8e',refunded:'#ef4444'}}/></td>
                    <td style={{ padding:'12px', color:'var(--text-muted)', fontSize:'0.75rem' }}>{new Date(o.created_at).toLocaleDateString('en-RW')}</td>
                    <td style={{ padding:'12px' }}>
                      <button onClick={()=>setModal({type:'order',data:o})} style={{ padding:'5px 12px', borderRadius:6, border:'1px solid var(--border)', background:'transparent', color:'var(--text-secondary)', fontSize:'0.78rem', cursor:'pointer', fontFamily:'inherit' }}>Manage</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {modal?.type==='product' && <ProductModal product={modal.data} onClose={()=>setModal(null)} onSaved={()=>{setModal(null);loadProducts();}}/>}
      {modal?.type==='order'   && <OrderModal   order={modal.data}   onClose={()=>setModal(null)} onUpdate={()=>{setModal(null);loadOrders();}}/>}
    </div>
  );
}